/* rk-wholesale-form: behaviour of sections/rk-wholesale-form.liquid (/pages/wholesale). Task 08e.
 *
 * Spec = live's GemPages form (Oct 2026): same payload keys, same Turnstile flow, same success/reset timing.
 * Changes vs live: inline accessible errors instead of alert(), URL/email/quantity checks, values trimmed,
 * amazonMarketplace sent empty when Amazon is unchecked, endpoint read from the section settings.
 *
 * Config (data attributes on [data-rk-wholesale]):
 *   data-endpoint  blank = "not connected": validates, logs the payload, sends nothing
 *   data-mode      cors-json = POST JSON, success only on a 2xx {success: true} (Cloudflare Worker)
 *                  no-cors   = fire-and-forget, the response can't be read (Google Apps Script)
 *   data-sitekey   Cloudflare Turnstile site key; blank = no security check
 *
 * All transport is in sendLead(), so a later "save customer in Shopify" only touches that function.
 * Only global: the Turnstile onload callback.
 */
(function () {
  'use strict';

  var TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=rkWholesaleTurnstileReady';
  var RESET_DELAY = 5000;
  var COPIED_DELAY = 2000;
  var REQUEST_TIMEOUT = 30000;
  var CHANNEL_URL_KEYS = {
    amazon: 'amazonUrl',
    walmart: 'walmartUrl',
    ebay: 'ebayUrl',
    shopify: 'shopifyUrl',
    instagram: 'instagramUrl',
    physical: 'physicalUrl',
    b2b: 'b2bUrl'
  };
  var TEXT = {
    sending: 'Sending...',
    copied: 'Copied!',
    required: 'Please fill in this field.',
    invalid: 'Please check this field.',
    channels: 'Please select at least one sales channel.',
    security: 'Please complete the security check before submitting.',
    sendError: 'Error sending application. Please complete the security check and try again.',
    notConnected: "This form isn't connected yet, so your application was not sent."
  };
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  var forms = [];

  function normalizeUrl(value) {
    var candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : 'https://' + value;
    try {
      var url = new URL(candidate);
      if (!/^https?:$/.test(url.protocol) || url.hostname.indexOf('.') < 1 || /\s/.test(value)) return '';
      return candidate;
    } catch (e) {
      return '';
    }
  }

  function applicationNumber() {
    var d = new Date();
    var pad = function (n, len) { return String(n).padStart(len, '0'); };
    return 'RSP-' + d.getFullYear() + pad(d.getMonth() + 1, 2) + pad(d.getDate(), 2) + '-' + pad(Math.floor(Math.random() * 100000), 5);
  }

  function WholesaleForm(root) {
    this.root = root;
    this.endpoint = (root.dataset.endpoint || '').trim();
    this.mode = root.dataset.mode === 'no-cors' ? 'no-cors' : 'cors-json';
    this.sitekey = (root.dataset.sitekey || '').trim();
    this.form = root.querySelector('[data-form]');
    this.submitBtn = root.querySelector('[data-submit]');
    this.resetBtn = root.querySelector('[data-reset]');
    this.success = root.querySelector('[data-success]');
    this.appNumber = root.querySelector('[data-app-number]');
    this.copyBtn = root.querySelector('[data-copy]');
    this.summary = root.querySelector('[data-summary]');
    this.summaryList = root.querySelector('[data-summary-list]');
    this.notice = root.querySelector('[data-notice]');
    this.channelsGroup = root.querySelector('[data-channels]');
    this.channelsError = root.querySelector('[data-channels-error]');
    this.tsSlot = root.querySelector('[data-turnstile-slot]');
    this.tsContainer = root.querySelector('[data-turnstile]');
    this.channels = Array.prototype.slice.call(root.querySelectorAll('input[name="channels"]'));
    this.submitLabel = this.submitBtn.textContent;
    this.copyLabel = this.copyBtn.textContent;
    this.token = '';
    this.widgetId = null;
    this.busy = false;
    this.errored = new Set();
    this.bind();
    this.channels.forEach(this.toggleChannel, this);
    this.watchTurnstileSlot();
    this.setSubmitEnabled(!this.sitekey);
  }

  WholesaleForm.prototype.bind = function () {
    var self = this;
    this.form.addEventListener('submit', function (e) { self.onSubmit(e); });
    this.form.addEventListener('reset', function () { setTimeout(function () { self.clearUi(); }); });
    this.resetBtn.addEventListener('click', function () {
      self.token = '';
      self.resetTurnstile();
      self.setSubmitEnabled(!self.sitekey);
    });
    this.copyBtn.addEventListener('click', function () { self.copyNumber(); });
    this.channels.forEach(function (cb) {
      cb.addEventListener('change', function () {
        self.toggleChannel(cb);
        if (self.errored.has(self.channelsGroup)) self.revalidate(self.channelsGroup);
      });
    });
    this.form.addEventListener('focusout', function (e) {
      var el = e.target;
      if (el.type === 'url' && el.value.trim()) {
        var normalized = normalizeUrl(el.value.trim());
        if (normalized) el.value = normalized;
      }
      if (self.errored.has(el)) self.revalidate(el);
    });
    this.form.addEventListener('change', function (e) {
      if (self.errored.has(e.target)) self.revalidate(e.target);
    });
    this.form.addEventListener('input', function (e) {
      if (self.errored.has(e.target) && !self.fieldError(e.target)) self.revalidate(e.target);
    });
    this.summaryList.addEventListener('click', function (e) {
      var link = e.target.closest('a');
      if (!link) return;
      var target = document.getElementById(link.getAttribute('href').slice(1));
      if (!target) return;
      e.preventDefault();
      target.focus();
    });
  };

  /* ---------------------------------------------------------------- Turnstile */
  WholesaleForm.prototype.renderTurnstile = function () {
    var self = this;
    if (!this.sitekey || this.widgetId !== null || !window.turnstile || !this.tsContainer.isConnected) return;
    var lost = function () {
      self.token = '';
      self.setSubmitEnabled(false);
      self.syncSlot();
    };
    this.widgetId = window.turnstile.render(this.tsContainer, {
      sitekey: this.sitekey,
      callback: function (token) {
        self.token = token;
        if (!self.busy) self.setSubmitEnabled(true);
        self.syncSlot();
      },
      'expired-callback': lost,
      'timeout-callback': lost,
      'error-callback': lost
    });
    this.syncSlot();
  };

  WholesaleForm.prototype.resetTurnstile = function () {
    if (window.turnstile && this.widgetId !== null) window.turnstile.reset(this.widgetId);
  };

  /* Approved deviation from live: collapse the 65px slot while the widget draws nothing. ResizeObserver does the
     work; the Turnstile callbacks re-sync too, as RO callbacks don't run in tabs Chrome treats as hidden. */
  WholesaleForm.prototype.syncSlot = function () {
    this.tsSlot.classList.toggle('rk-wf__ts-slot--empty', this.tsContainer.getBoundingClientRect().height < 1);
  };

  WholesaleForm.prototype.watchTurnstileSlot = function () {
    var self = this;
    this.syncSlot();
    if (window.ResizeObserver) new ResizeObserver(function () { self.syncSlot(); }).observe(this.tsContainer);
  };

  WholesaleForm.prototype.setSubmitEnabled = function (on) {
    this.submitBtn.disabled = !on;
  };

  /* ---------------------------------------------------------------- channels */
  WholesaleForm.prototype.toggleChannel = function (cb) {
    var self = this;
    var detail = this.root.querySelector('[data-channel-detail="' + cb.dataset.channel + '"]');
    if (!detail) return;
    detail.hidden = !cb.checked;
    detail.querySelectorAll('input, select').forEach(function (field) {
      field.disabled = !cb.checked;
      if (!cb.checked) self.showError(field, '');
    });
  };

  /* ---------------------------------------------------------------- validation */
  WholesaleForm.prototype.fieldError = function (el) {
    if (el === this.channelsGroup) {
      return this.channels.some(function (cb) { return cb.checked; }) ? '' : TEXT.channels;
    }
    if (el.disabled || !el.name) return '';
    var required = el.dataset.msgRequired || TEXT.required;
    var invalid = el.dataset.msgInvalid || TEXT.invalid;
    if (el.type === 'checkbox') return el.required && !el.checked ? required : '';
    if (el.validity && el.validity.badInput) return invalid;
    var value = el.value.trim();
    if (!value) return el.required ? required : '';
    if (el.type === 'email' && !EMAIL_RE.test(value)) return invalid;
    if (el.type === 'url' && !normalizeUrl(value)) return invalid;
    if (el.type === 'number') {
      var n = Number(value);
      if (!Number.isInteger(n) || n < Number(el.min || 1)) return invalid;
    }
    return '';
  };

  WholesaleForm.prototype.showError = function (el, message) {
    var errorEl = el === this.channelsGroup ? this.channelsError : document.getElementById(el.id + '-error');
    if (message) {
      this.errored.add(el);
      if (el !== this.channelsGroup) el.setAttribute('aria-invalid', 'true');
    } else {
      this.errored.delete(el);
      el.removeAttribute('aria-invalid');
    }
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = !message;
    }
  };

  WholesaleForm.prototype.validatable = function () {
    var fields = Array.prototype.slice.call(this.form.querySelectorAll('input, select, textarea'));
    var self = this;
    var list = [];
    fields.forEach(function (el) {
      if (el.name === 'channels') {
        if (list.indexOf(self.channelsGroup) === -1) list.push(self.channelsGroup);
        return;
      }
      if (el.required || el.type === 'url' || el.type === 'email' || el.type === 'number') list.push(el);
    });
    return list;
  };

  WholesaleForm.prototype.validateAll = function () {
    var self = this;
    var errors = [];
    this.validatable().forEach(function (el) {
      var message = self.fieldError(el);
      self.showError(el, message);
      if (message) errors.push({ el: el, focus: el === self.channelsGroup ? self.channels[0] : el, message: message });
    });
    return errors;
  };

  WholesaleForm.prototype.revalidate = function (el) {
    this.showError(el, this.fieldError(el));
    if (!this.summary.hidden) {
      if (this.errored.size) this.renderSummary(this.currentErrors());
      else this.summary.hidden = true;
    }
  };

  WholesaleForm.prototype.currentErrors = function () {
    var self = this;
    return this.validatable().filter(function (el) { return self.errored.has(el); }).map(function (el) {
      return { el: el, focus: el === self.channelsGroup ? self.channels[0] : el, message: self.fieldError(el) };
    });
  };

  WholesaleForm.prototype.renderSummary = function (errors) {
    var list = this.summaryList;
    list.textContent = '';
    errors.forEach(function (err) {
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = '#' + err.focus.id;
      a.textContent = err.message;
      li.appendChild(a);
      list.appendChild(li);
    });
    this.summary.hidden = !errors.length;
  };

  /* ---------------------------------------------------------------- submit */
  WholesaleForm.prototype.onSubmit = function (e) {
    e.preventDefault();
    if (this.busy) return;
    this.showNotice('');
    var errors = this.validateAll();
    if (errors.length) {
      this.renderSummary(errors);
      errors[0].focus.focus();
      return;
    }
    this.summary.hidden = true;
    if (this.sitekey && !this.token) {
      this.showNotice(TEXT.security);
      return;
    }
    this.submit();
  };

  WholesaleForm.prototype.submit = function () {
    var self = this;
    var payload = this.buildPayload();
    this.busy = true;
    this.submitBtn.textContent = TEXT.sending;
    this.setSubmitEnabled(false);
    this.sendLead(payload).then(function (sent) {
      if (sent) {
        self.onSuccess(payload.applicationNumber);
        return;
      }
      self.busy = false;
      self.submitBtn.textContent = self.submitLabel;
      self.setSubmitEnabled(!self.sitekey || !!self.token);
      self.showNotice(TEXT.notConnected);
    }).catch(function (error) {
      console.error('[rk-wholesale] Submission failed:', error);
      self.onFailure();
    });
  };

  WholesaleForm.prototype.buildPayload = function () {
    var form = this.form;
    var value = function (name) {
      var el = form.elements[name];
      return el && !el.disabled ? el.value.trim() : '';
    };
    var checked = this.channels.filter(function (cb) { return cb.checked; });
    var data = {
      applicationNumber: applicationNumber(),
      turnstileToken: this.token,
      firstName: value('firstName'),
      lastName: value('lastName'),
      email: value('email'),
      phone: value('phone'),
      companyName: value('companyName'),
      country: value('country'),
      city: value('city'),
      website: value('website'),
      channels: checked.map(function (cb) { return cb.value; }).join(', '),
      estimatedQuantity: value('estimatedQuantity'),
      frequency: value('frequency'),
      businessModel: value('businessModel'),
      productLinks: value('productLinks'),
      amazonMarketplace: value('amazonMarketplace'),
      date: new Date().toLocaleString()
    };
    checked.forEach(function (cb) {
      var key = CHANNEL_URL_KEYS[cb.dataset.channel];
      if (!key || !form.elements[key]) return;
      var v = value(key);
      data[key] = form.elements[key].type === 'url' && v ? normalizeUrl(v) : v;
    });
    return data;
  };

  /* Resolves true when the lead went out, false when no endpoint is set; rejects on failure. */
  WholesaleForm.prototype.sendLead = function (payload) {
    if (!this.endpoint) {
      console.info('[rk-wholesale] No endpoint set, nothing was sent. Payload:', payload);
      return Promise.resolve(false);
    }
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, REQUEST_TIMEOUT);
    var request;
    if (this.mode === 'no-cors') {
      request = fetch(this.endpoint, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(payload),
        signal: controller.signal
      }).then(function () { return true; });
    } else {
      request = fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal
      }).then(function (response) {
        return response.json().catch(function () {
          throw new Error('Invalid response from server');
        }).then(function (result) {
          if (!response.ok || !result || !result.success) throw new Error('Submission rejected (' + response.status + ')');
          return true;
        });
      });
    }
    return request.finally(function () { clearTimeout(timer); });
  };

  WholesaleForm.prototype.onSuccess = function (number) {
    var self = this;
    this.appNumber.textContent = number;
    this.success.hidden = false;
    this.success.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.success.focus({ preventScroll: true });
    this.token = '';
    this.resetTurnstile();
    setTimeout(function () {
      self.form.reset();
      self.success.hidden = true;
      self.submitBtn.textContent = self.submitLabel;
      self.busy = false;
      self.setSubmitEnabled(!self.sitekey || !!self.token);
    }, RESET_DELAY);
  };

  WholesaleForm.prototype.onFailure = function () {
    this.token = '';
    this.busy = false;
    this.submitBtn.textContent = this.submitLabel;
    this.resetTurnstile();
    this.setSubmitEnabled(!this.sitekey);
    this.showNotice(TEXT.sendError);
  };

  WholesaleForm.prototype.showNotice = function (message) {
    this.notice.textContent = message;
    this.notice.hidden = !message;
  };

  /* After a native form reset (Clear button or the post-success reset). */
  WholesaleForm.prototype.clearUi = function () {
    var self = this;
    Array.from(this.errored).forEach(function (el) { self.showError(el, ''); });
    this.summary.hidden = true;
    this.showNotice('');
    this.channels.forEach(this.toggleChannel, this);
  };

  WholesaleForm.prototype.copyNumber = function () {
    var self = this;
    var number = this.appNumber.textContent.trim();
    var done = function () {
      self.copyBtn.textContent = TEXT.copied;
      setTimeout(function () { self.copyBtn.textContent = self.copyLabel; }, COPIED_DELAY);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(number).then(done, function () { self.selectNumber(); });
    } else {
      this.selectNumber();
    }
  };

  WholesaleForm.prototype.selectNumber = function () {
    var range = document.createRange();
    range.selectNodeContents(this.appNumber);
    var selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  };

  /* ---------------------------------------------------------------- init */
  window.rkWholesaleTurnstileReady = function () {
    forms.forEach(function (f) { f.renderTurnstile(); });
  };

  function loadTurnstile() {
    if (window.turnstile) {
      window.rkWholesaleTurnstileReady();
      return;
    }
    if (document.querySelector('script[data-rk-turnstile]')) return;
    var script = document.createElement('script');
    script.src = TURNSTILE_SRC;
    script.async = true;
    script.setAttribute('data-rk-turnstile', '');
    document.head.appendChild(script);
  }

  function init(scope) {
    scope.querySelectorAll('[data-rk-wholesale]').forEach(function (root) {
      if (root.rkWholesale) return;
      root.rkWholesale = new WholesaleForm(root);
      forms.push(root.rkWholesale);
    });
    if (forms.some(function (f) { return f.sitekey; })) loadTurnstile();
  }

  init(document);
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
